import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, Camera, ArrowsClockwise, Lightning } from "@phosphor-icons/react";

const REGION_ID = "barcode-scanner-region";

export default function BarcodeScanner({ open, onClose, onScan }) {
    const scannerRef = useRef(null);
    const [error, setError] = useState("");
    const [needsStart, setNeedsStart] = useState(true);
    const [starting, setStarting] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [facingMode, setFacingMode] = useState("environment");

    const stopScanner = async () => {
        const sc = scannerRef.current;
        scannerRef.current = null;
        if (sc) {
            try {
                if (sc.isScanning) await sc.stop();
                await sc.clear();
            } catch {
                // ignore
            }
        }
        setScanning(false);
    };

    const translateError = (err) => {
        const name = err?.name || "";
        const msg = err?.message || String(err || "");
        const blob = `${name} ${msg}`.toLowerCase();
        if (
            /permission|notallowed|denied|blocked/.test(blob)
        ) {
            return "Άρνηση πρόσβασης στην κάμερα. Πατήστε 🔒 δίπλα στη διεύθυνση → Άδειες → Κάμερα → Επιτρέπεται και ξαναπροσπαθήστε.";
        }
        if (/notfound|devicesnotfound|no camera/.test(blob)) {
            return "Δεν εντοπίστηκε κάμερα στη συσκευή.";
        }
        if (/notreadable|trackstart|in use/.test(blob)) {
            return "Η κάμερα χρησιμοποιείται από άλλη εφαρμογή. Κλείστε την και ξαναπροσπαθήστε.";
        }
        if (/secure|https/.test(blob)) {
            return "Η κάμερα απαιτεί ασφαλή σύνδεση (HTTPS).";
        }
        if (/overconstrain/.test(blob)) {
            return "Η ζητούμενη κάμερα δεν είναι διαθέσιμη. Δοκιμάστε «Αλλαγή κάμερας».";
        }
        // Show raw message + name so we can debug
        return msg
            ? `${msg}${name ? " · " + name : ""}`
            : "Δεν ήταν δυνατή η εκκίνηση της κάμερας.";
    };

    const startScanner = async (mode = facingMode) => {
        setError("");
        setStarting(true);
        try {
            await stopScanner();
            if (
                typeof window !== "undefined" &&
                window.isSecureContext === false
            ) {
                throw new Error(
                    "Η πρόσβαση στην κάμερα απαιτεί HTTPS."
                );
            }
            if (
                !navigator.mediaDevices ||
                !navigator.mediaDevices.getUserMedia
            ) {
                throw new Error(
                    "Ο browser σας δεν υποστηρίζει πρόσβαση σε κάμερα."
                );
            }

            const scanner = new Html5Qrcode(REGION_ID, { verbose: false });
            scannerRef.current = scanner;
            await scanner.start(
                { facingMode: mode },
                {
                    fps: 10,
                    qrbox: { width: 260, height: 160 },
                },
                (decodedText) => {
                    onScan(String(decodedText).trim());
                    stopScanner().finally(() => onClose());
                },
                () => {
                    // ignore per-frame failures
                }
            );
            setScanning(true);
            setNeedsStart(false);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[Scanner] start failed:", err);
            setError(translateError(err));
            setNeedsStart(true);
        } finally {
            setStarting(false);
        }
    };

    useEffect(() => {
        if (open) {
            setNeedsStart(true);
            setError("");
        } else {
            stopScanner();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleSwitchCamera = async () => {
        const next = facingMode === "environment" ? "user" : "environment";
        setFacingMode(next);
        await startScanner(next);
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => {
                if (!o) {
                    stopScanner().finally(onClose);
                }
            }}
        >
            <DialogContent
                className="rounded-none border-2 border-[#0A0A0A] max-w-lg p-0 gap-0"
                data-testid="barcode-scanner-dialog"
            >
                <DialogHeader className="p-5 border-b border-[#E2E4E9] flex-row items-start justify-between space-y-0">
                    <div>
                        <DialogTitle className="font-heading text-xl font-black tracking-tighter text-[#0A0A0A] flex items-center gap-2">
                            <Camera size={20} weight="bold" />
                            Σάρωση Barcode
                        </DialogTitle>
                        <DialogDescription className="text-xs text-[#5C5F66] mt-1">
                            Ευθυγραμμίστε το barcode μέσα στο πλαίσιο.
                        </DialogDescription>
                    </div>
                    {scanning && (
                        <button
                            onClick={handleSwitchCamera}
                            data-testid="scanner-switch-camera"
                            className="p-2 border border-[#E2E4E9] hover:border-[#0A0A0A] hover:bg-[#F4F5F7] transition-colors"
                            title="Αλλαγή κάμερας"
                        >
                            <ArrowsClockwise size={16} weight="bold" />
                        </button>
                    )}
                </DialogHeader>

                <div className="relative bg-[#0A0A0A] aspect-[4/3] flex items-center justify-center overflow-hidden">
                    <div
                        id={REGION_ID}
                        className="w-full h-full"
                        data-testid="scanner-region"
                    />
                    {!scanning && !starting && needsStart && !error && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center">
                            <Camera
                                size={48}
                                weight="thin"
                                className="text-white/70"
                            />
                            <div className="text-white/80 text-sm max-w-xs">
                                Πατήστε το παρακάτω κουμπί για να ενεργοποιήσετε
                                την κάμερα. Ο browser θα ζητήσει την άδειά σας.
                            </div>
                            <Button
                                onClick={() => startScanner()}
                                data-testid="scanner-start-button"
                                className="rounded-none h-11 px-6 bg-white text-[#0A0A0A] hover:bg-[#F4F5F7] font-bold transition-all duration-200 active:scale-[0.98]"
                            >
                                <Lightning
                                    size={16}
                                    weight="bold"
                                    className="mr-2"
                                />
                                Ενεργοποίηση κάμερας
                            </Button>
                        </div>
                    )}
                    {starting && (
                        <div className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold uppercase tracking-[0.2em]">
                            Ενεργοποιηση καμερας...
                        </div>
                    )}
                    {error && (
                        <div className="absolute inset-0 bg-[#0A0A0A]/95 flex flex-col items-center justify-center p-6 gap-4 overflow-y-auto">
                            <div className="text-center max-w-xs">
                                <div className="text-[#FF6B6B] text-xs font-bold uppercase tracking-[0.2em] mb-2">
                                    Σφαλμα
                                </div>
                                <div className="text-white text-sm leading-relaxed break-words">
                                    {error}
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    onClick={() => startScanner()}
                                    data-testid="scanner-retry-button"
                                    className="rounded-none h-10 px-5 bg-white text-[#0A0A0A] hover:bg-[#F4F5F7] font-bold transition-all duration-200 active:scale-[0.98]"
                                >
                                    Δοκιμάστε ξανά
                                </Button>
                                <Button
                                    onClick={() =>
                                        startScanner(
                                            facingMode === "environment"
                                                ? "user"
                                                : "environment"
                                        )
                                    }
                                    data-testid="scanner-try-other-camera"
                                    variant="outline"
                                    className="rounded-none h-10 px-5 border-white text-white bg-transparent hover:bg-white/10 font-bold"
                                >
                                    Άλλη κάμερα
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-[#E2E4E9] flex justify-end">
                    <Button
                        onClick={() => stopScanner().finally(onClose)}
                        variant="outline"
                        data-testid="scanner-close-button"
                        className="rounded-none h-10 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#F4F5F7]"
                    >
                        <X size={16} weight="bold" className="mr-2" />
                        Κλείσιμο
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
